import React, {type ReactNode} from 'react'
import {type IAuthState} from '@/Types/auth'
import {Category, Event, Organization} from '@duesyco/shared'
import Lightweight from '@/Layouts/Lightweight'
import {Link} from '@inertiajs/react'
import {route} from 'ziggy-js'
import {dateStringForEvent} from '@/Utils/DateTime'
import EventCategoryPill from '@/Components/Assets/EventCategoryPill'
import EventLocationPill from '@/Components/Assets/EventLocationPill'

interface Props {
    auth: IAuthState
    events: Event[]
    organization: Organization
    category?: Category
    logo_url: string | null
}

const TicketListOrganization = (
    {
        auth,
        events,
        organization,
        category,
        logo_url = null,
    }: Props): ReactNode => {
    return <Lightweight auth={auth} logoUrl={logo_url}>
        <div className={'max-w-full sm:max-w-md px-6 pt-8 space-y-6 pb-28 mx-auto'}>
            <div className={'space-y-1'}>
                <div className={'font-bold text-2xl'} data-testid={'organization-name'}>
                    {organization.name}
                </div>
                {category && (
                    <div className={'text-lg text-storm-40'} data-testid={'category-name'}>
                        {category.name}
                    </div>
                )}
            </div>
            <div className={'divide-y divide-solid'} data-testid={'event-listing'}>
                {events.map((event) => (
                    <Link
                        key={event.id}
                        className={'flex flex-row items-center gap-3 py-4 cursor-pointer'}
                        href={route('tickets.show', {event: event.id})}
                    >
                        {event.thumbnail?.url && (
                            <div className={'w-20 shrink-0 self-start flex justify-center'}>
                                <img
                                    src={event.thumbnail.url}
                                    alt=""
                                    className={'max-w-full rounded-lg'}
                                />
                            </div>
                        )}
                        <div className={'grow space-y-1'}>
                            <div className={'font-bold'}>
                                {event.name}
                            </div>
                            <div className={'text-xs'}>
                                {dateStringForEvent(event, 'EEEE, M/d/yy')}
                            </div>
                            {(event.category || event.locations?.length === 1) && (
                                <div className={'flex flex-wrap items-center gap-1'}>
                                    {event.locations?.length === 1 && (
                                        <EventLocationPill location={event.locations[0]}
                                                           testId={event.id + '-location-' + event.locations[0].id}/>
                                    )}
                                    {event.category && <EventCategoryPill category={event.category}
                                                                            testId={event.id + '-category-' + event.category.id}/>}
                                </div>
                            )}
                        </div>
                        <div aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                 strokeWidth="1.5"
                                 stroke="currentColor" className="size-6">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/>
                            </svg>
                        </div>
                    </Link>
                ))}
                {events.length === 0 && (
                    <div
                        className={'rounded-lg border-dashed border-2 py-8 space-y-2 flex flex-col justify-center items-center'}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                             className="size-8 text-storm-60">
                            <path fillRule="evenodd"
                                  d="M1.5 6.375c0-1.036.84-1.875 1.875-1.875h17.25c1.035 0 1.875.84 1.875 1.875v3.026a.75.75 0 0 1-.375.65 2.249 2.249 0 0 0 0 3.898.75.75 0 0 1 .375.65v3.026c0 1.035-.84 1.875-1.875 1.875H3.375A1.875 1.875 0 0 1 1.5 17.625v-3.026a.75.75 0 0 1 .374-.65 2.249 2.249 0 0 0 0-3.898.75.75 0 0 1-.374-.65V6.375Zm15-1.125a.75.75 0 0 1 .75.75v.75a.75.75 0 0 1-1.5 0V6a.75.75 0 0 1 .75-.75Zm.75 4.5a.75.75 0 0 0-1.5 0v.75a.75.75 0 0 0 1.5 0v-.75Zm-.75 3a.75.75 0 0 1 .75.75v.75a.75.75 0 0 1-1.5 0v-.75a.75.75 0 0 1 .75-.75Zm.75 4.5a.75.75 0 0 0-1.5 0V18a.75.75 0 0 0 1.5 0v-.75ZM6 12a.75.75 0 0 1 .75-.75H12a.75.75 0 0 1 0 1.5H6.75A.75.75 0 0 1 6 12Zm.75 2.25a.75.75 0 0 0 0 1.5h3a.75.75 0 0 0 0-1.5h-3Z"
                                  clipRule="evenodd"/>
                        </svg>
                        <p>No events at the moment.</p>
                    </div>
                )}
            </div>
        </div>
    </Lightweight>
}

export default TicketListOrganization
